<template>
  <Teleport to="body">
    <div
      v-if="model"
      class="fixed top-0 left-0 right-0 bottom-0 bg-gray-500/50 backdrop-blur p-4 flex items-center justify-center z-[999]"
      @click.self="model = false"
      @dragover.prevent
      @drop.prevent="dataIsDragging = false; methodFileSelected($event)"
      >
      <div
        class="w-[400px] aspect-video bg-white !p-2 ui-card"
        :class="containerClass"
        @dragover="dataIsDragging = true"
        @dragleave="dataIsDragging = false"
        @dragend="dataIsDragging = false"
        @mouseleave="dataIsDragging = false"
        @click="refInput?.click()"
        >
        <div
          class="border-2 border-dashed rounded-md flex flex-col items-center justify-center h-full gap-2"
          :class="[
            dataIsDragging
              ? 'border-blue-500 bg-sky-100' :
                'border-gray-300'
          ]"
          @drop.prevent="dataIsDragging = false; methodFileSelected($event)"
          >
          <span>Drop files</span>
          <span class="text-gray-400">or</span>
          <span>Click to select</span>
        </div>
      </div>
      <input
        ref="refInput"
        type="file"
        class="!hidden"
        @change.stop.prevent="methodFileSelected"
        >
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { forEach as useForEach } from 'lodash-es'

const emit = defineEmits(['file'])
const props = withDefaults(defineProps<{
	types?: string|string[],
	extension?: string|string[],
	size?: number,
  containerClass?: string,
}>(),{
  types: () => [
    'image',
    'text',
  ],
  size: 1024 * 1024 * 2,
})

const model = defineModel<boolean>()
const modelUploading = defineModel<boolean>('uploading')
const refInput = ref<HTMLInputElement>()
const dataIsDragging = ref<boolean>(false)
const dataUploadingIds = ref<string[]>([])

const methodFileSelected = (event: Event|any) => {
  event.preventDefault()
  event.stopPropagation()

 	// if is dragevent
  if (event.dataTransfer) {
    modelUploading.value = true
    console.log(event.dataTransfer.files)

    useForEach(event.dataTransfer.files, (file: File) => {
      methodUploadFile(file)
    })
  }

  // if input type change
  if (event.target.files) {
    modelUploading.value = true
    useForEach(event.target.files, (file: File) => {
      methodUploadFile(file)
    })
  }
}

const methodUploadFile = async (file: File) => {
  try{
    methodValidateType(file)
  }catch(error){
    return notify({
      text: error as string,
      type: 'error',
    })
  }

  // if(file.size > props.size){
  // 	return notify({
  // 		text: 'File size is too large. Maximum of '+Math.round(props.size/(1024*1024))+'MB allowed. File is '+Math.round((file.size-props.size)/(1024))+'kB too large.',
  // 		type: 'error',
  // 	})
  // }

  model.value = false

  const uploadId = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);

  dataUploadingIds.value.push(uploadId)

  const reader = file.stream().getReader();
  let chunks: {[key:number]: number} = []; // Array to store all chunks of data

  notify({
    id: 'uploading'+uploadId,
    text: 'Reading file ...',
    timeout: 5000,
  })

  let done = false;
  while (!done) {
    const { value, done: isDone } = await reader.read(); // Wait for the next chunk
    if (value) {
      chunks.push(value); // Collect the chunk
    }
    done = isDone; // Update the loop condition
  }

  notify({
    id: 'uploading'+uploadId,
    text: 'Optimizing file ...',
    timeout: 5000,
  })

  // combine chunks which are smaller than 100kB
  chunks = chunks.reduce((acc, chunk) => {
    if(acc.length == 0){
      return [chunk]
    }

    if(acc[acc.length - 1].length + chunk.length < (1024 * 100)){
      acc[acc.length - 1] = new Uint8Array([...acc[acc.length - 1], ...chunk])
    }else{
      acc.push(chunk)
    }

    return acc
  }, [])

  for (let index = 0; index < chunks.length; index++) {
    const chunk = chunks[index];
    const isLast = index == chunks.length - 1;

    console.log(chunk.length)

    await ws.send('file.split',{
      index,
      uploadId,
      chunk,
      name: file.name,
      type: file.type,
      size: file.size,
      isLast,
    },null,(progress) => {
      console.log(progress)
    }).then((response) => {
      if(isLast){
        notify({
          id: 'uploading'+uploadId,
          text: response?.message ?? response,
          timeout: 3000,
        })

        emit('file', response.file)
      }else{
        notify({
          id: 'uploading'+uploadId,
          text: 'Uploaded part '+index+'/'+(chunks.length - 1)+' ...',
          type: 'progress',
        })
      }

      if (isLast){
        dataUploadingIds.value = dataUploadingIds.value.filter((id) => id != uploadId)
      }
    }).catch((e) => {
      api.parseError(e)

      dataUploadingIds.value = dataUploadingIds.value.filter((id) => id != uploadId)
    }).finally(() => {
      if (dataUploadingIds.value.length <= 0){
        modelUploading.value = false
      }
    })
  }

}

const methodValidateType = (file: any) => {
  if(!props.types){
    return false
  }

  const checkAllowedType = (type: string, file: any) => {
    let parts = type.split('/').length

    if(parts >= 2 && type.split('/')[1] == '*'){
      parts = 1
    }

    if (parts <= 0) {
      return 'File type not valid'
    }

    if(parts == 1 && file.type.split('/')[0] != type){
      return 'Invalid file type '+file.type
    }

    if(parts == 2 && file.type != type){
      return 'Invalid file type '+file.type
    }

    return true;
  }

  if(props.types){
    // if props.types is a string
    if(typeof props.types == 'string'){
      const result = checkAllowedType(props.types, file.type);

      if(result){
        throw result
      }
    }

    // if props.types is an array
    if(typeof props.types == 'object'){
      const checks: (string|boolean)[] = []

      props.types.forEach((type) => {
        checks.push(checkAllowedType(type, file))
      })

      if(checks.filter((check) => check === true).length == 0){
        throw 'Invalid file type '+file.type
      }
    }
  }
}

defineExpose({
  open: () => model.value = true,
  close: () => model.value = false,
})
</script>
